/**
 * URL Attribution Capture — extracts tracking parameters from the current URL.
 *
 * Captures: utm_source, utm_medium, utm_campaign, utm_content, utm_term,
 * gclid (Google Ads), fbclid (Meta), src, ref, cta, service,
 * plus first-touch landing_page and referrer.
 *
 * Used by all lead-generating forms to write attribution into both metadata
 * and (where the schema allows) first-class lead columns.
 */

const ATTRIBUTION_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "gclid", "fbclid", "msclkid", "src", "ref", "cta", "service",
] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  content_ref?: string;
  cta_variant?: string;
  attributed_service?: string;
  /** First page the visitor landed on (set once, never overwritten in-session). */
  landing_page?: string;
  /** document.referrer captured at first landing. */
  referrer?: string;
  /** When the first touch was recorded (ISO). */
  first_touch_at?: string;
  /** Most recent marketing arrival (campaign/click id/external referrer). */
  latest_touch?: LatestTouch;
}

export type LatestTouch = Omit<Attribution, "latest_touch" | "first_touch_at"> & { captured_at: string };

const LATEST_KEY = "_evo_attr_latest_touch";

function readLatest(): LatestTouch | null {
  try {
    const raw = localStorage.getItem(LATEST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LatestTouch;
    if (!parsed?.captured_at) return null;
    const age = Date.now() - new Date(parsed.captured_at).getTime();
    if (!Number.isFinite(age) || age < 0 || age > 30 * 24 * 60 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

function readFirstTouchAt(): string | undefined {
  try {
    const raw = localStorage.getItem("_evo_attr_first_touch");
    return raw ? (JSON.parse(raw)?.captured_at as string | undefined) : undefined;
  } catch {
    return undefined;
  }
}

/** Capture attribution from URL search params only (no landing/referrer). */
export function captureAttribution(searchParams?: URLSearchParams): Attribution | null {
  const params = searchParams || new URLSearchParams(window.location.search);
  const attr: Record<string, string> = {};

  for (const key of ATTRIBUTION_KEYS) {
    const val = params.get(key);
    if (val) {
      if (key === "src") attr.utm_source = val;
      else if (key === "ref") attr.content_ref = val;
      else if (key === "cta") attr.cta_variant = val;
      else if (key === "service") attr.attributed_service = val;
      else attr[key] = val;
    }
  }

  return Object.keys(attr).length > 0 ? attr : null;
}

const SESSION_KEY = "_evo_attr";
/**
 * Durable copy of the same first-touch record. sessionStorage alone is lost
 * when the visitor signs in through a redirect that opens a new tab, or comes
 * back later to finish an enquiry, which silently destroys the source of the
 * eventual lead. The durable copy keeps that first touch for 30 days.
 */
const DURABLE_KEY = "_evo_attr_first_touch";
const DURABLE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface DurableRecord {
  attribution: Attribution;
  captured_at: string;
}

function readDurable(): Attribution | null {
  try {
    const raw = localStorage.getItem(DURABLE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DurableRecord;
    if (!parsed?.attribution || !parsed.captured_at) return null;
    const age = Date.now() - new Date(parsed.captured_at).getTime();
    if (!Number.isFinite(age) || age < 0 || age > DURABLE_MAX_AGE_MS) {
      localStorage.removeItem(DURABLE_KEY);
      return null;
    }
    return parsed.attribution;
  } catch {
    return null;
  }
}

function safeParse(raw: string | null): Attribution {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Attribution;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeDurable(attribution: Attribution): void {
  try {
    localStorage.setItem(
      DURABLE_KEY,
      JSON.stringify({ attribution, captured_at: new Date().toISOString() } satisfies DurableRecord),
    );
  } catch {
    // storage unavailable — the session copy still applies
  }
}

/**
 * Persist first-touch attribution + landing page + referrer.
 * Runs once per visit — does not overwrite an earlier first touch, and keeps a
 * durable 30-day copy so a sign-in redirect or a later return still credits the
 * original source.
 */
export function persistAttribution(): void {
  if (typeof window === "undefined") return;

  // Latest touch: every arrival that carries a campaign/click identifier or an
  // external referrer replaces the previous latest touch. First touch is kept
  // separately and never overwritten.
  try {
    const urlSignals = captureAttribution() || {};
    const extRef = document.referrer && !document.referrer.startsWith(window.location.origin)
      ? document.referrer
      : "";
    if (Object.keys(urlSignals).length > 0 || extRef) {
      const latest: LatestTouch = {
        ...urlSignals,
        landing_page: window.location.href,
        ...(extRef ? { referrer: extRef } : {}),
        captured_at: new Date().toISOString(),
      };
      localStorage.setItem(LATEST_KEY, JSON.stringify(latest));
    }
  } catch {
    // storage unavailable — first touch still applies
  }

  let sessionRaw: string | null = null;
  try {
    sessionRaw = sessionStorage.getItem(SESSION_KEY);
  } catch {
    sessionRaw = null;
  }

  const durable = readDurable();

  // Already recorded for this visit: only heal a missing durable copy.
  if (sessionRaw) {
    if (!durable) writeDurable(safeParse(sessionRaw));
    return;
  }

  const urlAttr = captureAttribution() || {};
  const landing_page = window.location.href;
  const referrer = document.referrer && !document.referrer.startsWith(window.location.origin)
    ? document.referrer
    : "";

  const thisVisit: Attribution = {
    ...urlAttr,
    landing_page,
    ...(referrer ? { referrer } : {}),
  };

  // A durable first touch wins for source fields; this visit only fills gaps.
  const payload: Attribution = durable ? { ...thisVisit, ...durable } : thisVisit;

  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  } catch {
    // storage unavailable — durable copy below still applies
  }
  if (!durable) writeDurable(payload);
}

/**
 * Get persisted (first-touch) attribution merged with current URL params.
 * Current URL params take priority for UTM values; landing_page and referrer
 * remain locked to the first-touch values, falling back to the durable copy and
 * finally to the current page so provenance is never simply blank.
 */
export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;

  let sessionRaw: string | null = null;
  try {
    sessionRaw = sessionStorage.getItem(SESSION_KEY);
  } catch {
    sessionRaw = null;
  }

  const durableAttr = readDurable() || {};
  const storedAttr: Attribution = { ...durableAttr, ...safeParse(sessionRaw) };
  const currentAttr = captureAttribution() || {};

  const merged: Attribution = {
    ...storedAttr,
    ...currentAttr,
    // Keep first-touch landing_page / referrer locked
    landing_page: storedAttr.landing_page || durableAttr.landing_page || window.location.href,
    referrer: storedAttr.referrer || durableAttr.referrer,
    first_touch_at: readFirstTouchAt(),
    latest_touch: readLatest() || undefined,
  };

  // Strip empty keys
  for (const k of Object.keys(merged) as (keyof Attribution)[]) {
    if (merged[k] === undefined || merged[k] === "") delete merged[k];
  }

  return Object.keys(merged).length > 0 ? merged : null;
}
